/**
 * Little-endian binary reader that mirrors LiteNetLib's NetDataReader exactly, so it decodes the bytes
 * the C# client/server produce.
 *
 * Reference: Client/Assets/LiteNetLib/Utils/NetDataReader.cs (symmetric with NetDataWriter):
 *  - string: read ushort n; n == 0 -> ""; otherwise the body is (n - 1) UTF-8 bytes.
 *  - long/ulong are 64-bit -> returned as BigInt.
 */
export class BinaryReader {
  private readonly view: DataView;
  private pos: number;
  private readonly end: number;

  private static readonly decoder = new TextDecoder('utf-8', { fatal: false });

  constructor(data: Uint8Array, offset = 0, length = data.length - offset) {
    // DataView spans the underlying buffer at the Uint8Array's own offset; pos/end are absolute within it.
    this.view = new DataView(data.buffer, data.byteOffset, data.byteLength);
    this.pos = offset;
    this.end = offset + length;
  }

  get position(): number {
    return this.pos;
  }

  get remaining(): number {
    return this.end - this.pos;
  }

  get hasMore(): boolean {
    return this.pos < this.end;
  }

  private need(n: number): void {
    if (this.pos + n > this.end) {
      throw new RangeError(`BinaryReader: read past end (need ${n}, have ${this.end - this.pos})`);
    }
  }

  getByte(): number {
    this.need(1);
    const v = this.view.getUint8(this.pos);
    this.pos += 1;
    return v;
  }

  getSByte(): number {
    this.need(1);
    const v = this.view.getInt8(this.pos);
    this.pos += 1;
    return v;
  }

  getBool(): boolean {
    return this.getByte() !== 0;
  }

  getShort(): number {
    this.need(2);
    const v = this.view.getInt16(this.pos, true);
    this.pos += 2;
    return v;
  }

  getUShort(): number {
    this.need(2);
    const v = this.view.getUint16(this.pos, true);
    this.pos += 2;
    return v;
  }

  getInt(): number {
    this.need(4);
    const v = this.view.getInt32(this.pos, true);
    this.pos += 4;
    return v;
  }

  getUInt(): number {
    this.need(4);
    const v = this.view.getUint32(this.pos, true);
    this.pos += 4;
    return v;
  }

  getLong(): bigint {
    this.need(8);
    const v = this.view.getBigInt64(this.pos, true);
    this.pos += 8;
    return v;
  }

  getULong(): bigint {
    this.need(8);
    const v = this.view.getBigUint64(this.pos, true);
    this.pos += 8;
    return v;
  }

  getFloat(): number {
    this.need(4);
    const v = this.view.getFloat32(this.pos, true);
    this.pos += 4;
    return v;
  }

  getDouble(): number {
    this.need(8);
    const v = this.view.getFloat64(this.pos, true);
    this.pos += 8;
    return v;
  }

  getBytes(length: number): Uint8Array {
    this.need(length);
    const out = new Uint8Array(this.view.buffer, this.view.byteOffset + this.pos, length).slice();
    this.pos += length;
    return out;
  }

  getString(): string {
    const n = this.getUShort();
    if (n === 0) return '';
    const size = n - 1;
    this.need(size);
    const slice = new Uint8Array(this.view.buffer, this.view.byteOffset + this.pos, size);
    const s = BinaryReader.decoder.decode(slice);
    this.pos += size;
    return s;
  }

  getUShortArray(): number[] {
    const n = this.getUShort();
    const out: number[] = new Array(n);
    for (let i = 0; i < n; i++) out[i] = this.getUShort();
    return out;
  }

  getIntArray(): number[] {
    const n = this.getUShort();
    const out: number[] = new Array(n);
    for (let i = 0; i < n; i++) out[i] = this.getInt();
    return out;
  }

  getStringArray(): string[] {
    const n = this.getUShort();
    const out: string[] = new Array(n);
    for (let i = 0; i < n; i++) out[i] = this.getString();
    return out;
  }

  /** ushort count then 8-byte LE longs — mirrors NetDataReader.GetLongArray (used by Ticket referenceIds). */
  getLongArray(): bigint[] {
    const n = this.getUShort();
    const out: bigint[] = new Array(n);
    for (let i = 0; i < n; i++) out[i] = this.getLong();
    return out;
  }
}
